import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Notification, NotificationRealtimePayload, mapMailToNotification } from '@/types/notification.types';
import { RealtimeChannel } from '@supabase/supabase-js';

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refetch: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const currentUserIdRef = useRef<string | null>(null);

  // Fetch iniziale delle notifiche
  const fetchNotifications = async (userId?: string | null) => {
    try {
      const resolvedUserId = userId ?? currentUserIdRef.current;

      console.log('[NotificationContext] fetchNotifications called | resolvedUserId:', resolvedUserId);
      console.trace('[NotificationContext] fetchNotifications call stack');

      if (!resolvedUserId) {
        setNotifications([]);
        return;
      }

      // Query sulla user_notifications (ultimi 10 giorni)
      const since = new Date();
      since.setDate(since.getDate() - 10);

      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', resolvedUserId)
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notifications:', error);
        return;
      }

      // Mappa i risultati da mail_list a Notification
      const mappedNotifications = (data || []).map(mapMailToNotification);
      setNotifications(mappedNotifications);
    } catch (error) {
      console.error('Error in fetchNotifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Setup realtime listener
  useEffect(() => {
    const removeChannel = async () => {
      if (channelRef.current) {
        await supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };

    const setupForUser = async (userId: string | null) => {
      console.log('[NotificationContext] setupForUser called | userId:', userId, '| currentUserIdRef:', currentUserIdRef.current);
      console.trace('[NotificationContext] setupForUser call stack');
      currentUserIdRef.current = userId; // set immediately before any await to prevent races
      await removeChannel();

      if (!userId) {
        setNotifications([]);
        setIsLoading(false);
        return;
      }

      await fetchNotifications(userId);

      // Crea channel per le notifiche dell'utente (ascolta user_notifications)
      const notificationChannel = supabase
        .channel(`notifications-channel-${userId}`)
        .on(
          'postgres_changes',
          {
            event: '*', // Ascolta INSERT, UPDATE, DELETE
            schema: 'public',
            table: 'user_notifications',
            filter: `user_id=eq.${userId}`,
          },
          (payload: NotificationRealtimePayload) => {
            console.log('Notification realtime event:', payload);

            if (payload.eventType === 'INSERT') {
              const notification = mapMailToNotification(payload.new);
              setNotifications((prev) => [notification, ...prev]);
            } else if (payload.eventType === 'UPDATE') {
              const notification = mapMailToNotification(payload.new);
              setNotifications((prev) =>
                prev.map((n) => (n.id === notification.id ? notification : n))
              );
            } else if (payload.eventType === 'DELETE' && payload.old) {
              setNotifications((prev) => prev.filter((n) => n.id !== payload.old?.id));
            }
          }
        )
        .subscribe();

      channelRef.current = notificationChannel;
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUserId = session?.user?.id ?? null;
      if (nextUserId === currentUserIdRef.current) {
        return;
      }

      void setupForUser(nextUserId);
    });

    // Cleanup
    return () => {
      subscription.unsubscribe();
      void removeChannel();
    };
  }, []);

  // Calcola notifiche non lette
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Segna una notifica come letta
  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('user_notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) {
        console.error('Error marking notification as read:', error);
        return;
      }

      // Aggiorna lo stato locale (il realtime aggiornerà comunque)
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
    } catch (error) {
      console.error('Error in markAsRead:', error);
    }
  };

  // Segna tutte le notifiche come lette
  const markAllAsRead = async () => {
    try {
      const userId = currentUserIdRef.current;
      if (!userId) return;

      const { error } = await supabase
        .from('user_notifications')
        .update({ read: true })
        .eq('user_id', userId)
        .eq('read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        return;
      }

      // Aggiorna lo stato locale
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error in markAllAsRead:', error);
    }
  };

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
